import { readingRenderSource } from '@mio/shared';
import { ChevronDown, Code2 } from 'lucide-react';
import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactMarkdown, { type Components, type ExtraProps } from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';

import { CodeLines } from '../code-lines';

import { CollabboardModel, StudyAidBlock } from './collabboard/learning-block';
import { LearningLab } from './learning-lab';
import { LearningSteps } from './learning-steps';
import { MermaidDiagram } from './mermaid-diagram';
import { headingsOf } from './outline';

import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import './markdown.css';

const VisualMarkdownContext = createContext(false);
/**
 * Set only for workshop briefs written in the standard template (a first `overview` section).
 * `scope` keys the learner's own Done-when ticks in this browser.
 */
const WorkshopMarkdownContext = createContext<{ scope: string } | null>(null);
/** The fixed block headings of the workshop template, each with a stable tone for styling. */
const WORKSHOP_BLOCKS: Record<string, string> = {
  'What you will build': 'goal',
  'What is already there': 'given',
  'What you are given': 'given',
  'What you will create': 'create',
  'Your task': 'task',
  'How to run it': 'run',
  'Test it yourself': 'test',
  'How you will test it': 'test',
  'Done when': 'done',
  'Learn first': 'learn',
};
const SHELL_LANGUAGES = new Set(['sh', 'bash', 'shell', 'console']);
const SourceContext = createContext({
  lineOffset: 0,
  headings: new Map<number, string>(),
  canonicalSource: '',
});
function useSourceId(node: ExtraProps['node']) {
  const { lineOffset } = useContext(SourceContext);
  return node?.position ? `block-L${node.position.start.line + lineOffset}` : undefined;
}
function MarkdownHeading({
  children,
  node,
  level,
  ...props
}: ComponentPropsWithoutRef<'h2'> & ExtraProps & { level: 1 | 2 | 3 | 4 | 5 | 6 }) {
  const source = useContext(SourceContext);
  const workshop = useContext(WorkshopMarkdownContext);
  const blockId = useSourceId(node);
  const Tag = `h${level}` as const;
  const line = node?.position ? node.position.start.line + source.lineOffset : 0;
  const block = workshop && level === 3 ? WORKSHOP_BLOCKS[textOf(children).trim()] : undefined;
  if (block)
    return (
      <Tag
        {...props}
        id={source.headings.get(line) ?? props.id}
        data-learning-block={blockId}
        data-workshop-block={block}
        className="workshop-block-heading"
      >
        {children}
      </Tag>
    );
  return (
    <Tag {...props} id={source.headings.get(line) ?? props.id} data-learning-block={blockId}>
      {children}
    </Tag>
  );
}
function MarkdownParagraph({ children, node }: ComponentPropsWithoutRef<'p'> & ExtraProps) {
  return <p data-learning-block={useSourceId(node)}>{children}</p>;
}

function textOf(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

function MarkdownPre({ children, node, ...rest }: ComponentPropsWithoutRef<'pre'> & ExtraProps) {
  const visual = useContext(VisualMarkdownContext);
  const workshop = useContext(WorkshopMarkdownContext);
  const sourceContext = useContext(SourceContext);
  const blockId = useSourceId(node);
  const child: ReactNode = Array.isArray(children) ? (children[0] as ReactNode) : children;
  if (isValidElement<{ className?: string; children?: ReactNode }>(child)) {
    const language = /language-([\w-]+)/.exec(child.props.className ?? '')?.[1];
    const source = textOf(child.props.children).trimEnd();
    if (language === 'collabboard-lab')
      return (
        <div data-learning-block={blockId}>
          <CollabboardModel source={source} />
        </div>
      );
    if (language === 'study-aid')
      return (
        <div data-learning-block={blockId}>
          <StudyAidBlock source={source} />
        </div>
      );
    if (language === 'learning-hint')
      return (
        <details className="lesson-code-disclosure" data-learning-block={blockId}>
          <summary>Need a starting point? Read an implementation hint</summary>
          <Markdown
            source={source}
            canonicalSource={sourceContext.canonicalSource}
            sourceStartLine={(node?.position?.start.line ?? 1) + sourceContext.lineOffset + 1}
            workshopScope={workshop?.scope}
          />
        </details>
      );
    if (language === 'learning-lab')
      return (
        <div data-learning-block={blockId}>
          <LearningLab source={source} />
        </div>
      );
    if (language === 'mermaid')
      return (
        <div data-learning-block={blockId}>
          <MermaidDiagram source={source} />
        </div>
      );
    if (language === 'learning-steps')
      return (
        <div data-learning-block={blockId}>
          <LearningSteps source={source} />
        </div>
      );
    if (workshop && (language === 'output' || SHELL_LANGUAGES.has(language ?? ''))) {
      const output = language === 'output';
      return (
        <figure
          className="workshop-io"
          data-io={output ? 'output' : 'run'}
          data-learning-block={blockId}
        >
          <figcaption>{output ? 'Expected output' : 'Run'}</figcaption>
          <pre {...rest}>
            <CodeLines
              code={source}
              language={output ? undefined : language}
              codeClassName={output ? undefined : child.props.className}
            />
          </pre>
        </figure>
      );
    }
    const lines = source.split('\n').length;
    const block = (
      <CodeLines code={source} language={language} codeClassName={child.props.className} />
    );
    if (visual && lines >= 12) {
      const label =
        language === 'text' || language === 'txt'
          ? 'Recorded output'
          : language === 'python'
            ? 'Run the Python example'
            : 'View the code example';
      return (
        <details className="lesson-code-disclosure" data-learning-block={blockId}>
          <summary>
            <Code2 size={14} aria-hidden="true" /> {label}
            <span>{lines} lines</span>
            <ChevronDown size={14} aria-hidden="true" />
          </summary>
          <pre {...rest}>{block}</pre>
        </details>
      );
    }
    return (
      <pre {...rest} data-learning-block={blockId}>
        {block}
      </pre>
    );
  }
  return (
    <pre {...rest} data-learning-block={blockId}>
      {children}
    </pre>
  );
}

function storedTick(key: string) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** A Done-when item the learner can tick; the tick is a reminder kept in this browser only. */
function WorkshopCheckItem({ storageKey, children }: { storageKey: string; children: ReactNode }) {
  const [checked, setChecked] = useState(() => storedTick(storageKey));
  return (
    <label className="workshop-check">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          const next = event.target.checked;
          setChecked(next);
          try {
            if (next) localStorage.setItem(storageKey, '1');
            else localStorage.removeItem(storageKey);
          } catch {
            // Storage may be unavailable (private window); the tick still shows for this visit.
          }
        }}
      />
      <span>{children}</span>
    </label>
  );
}

function MarkdownListItem({
  children,
  node: _node,
  className,
  ...rest
}: ComponentPropsWithoutRef<'li'> & ExtraProps) {
  const workshop = useContext(WorkshopMarkdownContext);
  if (!workshop || !className?.includes('task-list-item'))
    return (
      <li className={className} {...rest}>
        {children}
      </li>
    );
  const content = Children.toArray(children).filter(
    (item) => !(isValidElement<{ type?: string }>(item) && item.props.type === 'checkbox'),
  );
  const label = textOf(content).trim();
  return (
    <li className={`${className} workshop-check-item`} {...rest}>
      <WorkshopCheckItem storageKey={`mio.workshop-done:${workshop.scope}:${label}`}>
        {content}
      </WorkshopCheckItem>
    </li>
  );
}

/**
 * Copies each column heading onto the body cells beneath it, so rows that stack in a narrow
 * panel keep their labels (see markdown.css). The table roles are stated explicitly because
 * stacked cells use display: block, which some browsers stop exposing as a table.
 */
function labelTableCells(table: HTMLTableElement) {
  const headings = Array.from(table.querySelectorAll('thead th')).map(
    (cell) => cell.textContent?.trim() ?? '',
  );
  table.setAttribute('role', 'table');
  for (const group of table.querySelectorAll('thead, tbody'))
    group.setAttribute('role', 'rowgroup');
  for (const row of table.querySelectorAll('tr')) row.setAttribute('role', 'row');
  for (const cell of table.querySelectorAll('thead th')) cell.setAttribute('role', 'columnheader');
  for (const row of table.querySelectorAll('tbody tr')) {
    Array.from(row.children).forEach((cell, index) => {
      cell.setAttribute('role', 'cell');
      cell.setAttribute('data-label', headings[index] ?? '');
    });
  }
}

function MarkdownTable({ children, node }: ComponentPropsWithoutRef<'table'> & ExtraProps) {
  const blockId = useSourceId(node);
  const table = useRef<HTMLTableElement>(null);
  useLayoutEffect(() => {
    if (table.current) labelTableCells(table.current);
  });
  return (
    <div
      className="lesson-table-scroll"
      role="region"
      aria-label="Comparison table"
      tabIndex={0}
      data-learning-block={blockId}
    >
      <table ref={table}>{children}</table>
    </div>
  );
}

const components: Components = {
  pre: MarkdownPre,
  a({ children, href, node: _node, ...props }) {
    // Reading a prerequisite must leave the Workshop editor and its buffers mounted.
    const reference = href?.startsWith('/studio/reference/');
    return (
      <a
        {...props}
        href={href}
        {...(reference ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
        {reference ? <span className="sr-only"> (opens lesson in a new tab)</span> : null}
      </a>
    );
  },
  p: MarkdownParagraph,
  li: MarkdownListItem,
  h1: (props) => <MarkdownHeading {...props} level={1} />,
  h2: (props) => <MarkdownHeading {...props} level={2} />,
  h3: (props) => <MarkdownHeading {...props} level={3} />,
  h4: (props) => <MarkdownHeading {...props} level={4} />,
  h5: (props) => <MarkdownHeading {...props} level={5} />,
  h6: (props) => <MarkdownHeading {...props} level={6} />,
  table: MarkdownTable,
};

/** Renders lesson Markdown with the platform's typography. Content comes from the database, never from users. */
export function Markdown({
  source,
  sourceStartLine = 1,
  canonicalSource = source,
  workshopScope,
}: {
  source: string;
  sourceStartLine?: number;
  canonicalSource?: string;
  /** Set for template workshop briefs: enables block headings, Run/Expected boxes and ticks. */
  workshopScope?: string | undefined;
}) {
  const visual = /^```learning-(?:steps|lab)\s*$/m.test(source);
  return (
    <SourceContext
      value={{
        lineOffset: sourceStartLine - 1,
        canonicalSource,
        headings: new Map(
          headingsOf(canonicalSource).map((heading) => [heading.sourceLine, heading.id]),
        ),
      }}
    >
      <VisualMarkdownContext value={visual}>
        <WorkshopMarkdownContext value={workshopScope ? { scope: workshopScope } : null}>
          <div
            className={visual ? 'prose-mio prose-visual' : 'prose-mio'}
            data-workshop-template={workshopScope ? '' : undefined}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSlug]}
              components={components}
            >
              {readingRenderSource(source)}
            </ReactMarkdown>
          </div>
        </WorkshopMarkdownContext>
      </VisualMarkdownContext>
    </SourceContext>
  );
}
